import Link from 'next/link';
import { ArrowRight } from 'lucide-react';

import type { GuideCategory } from '@/lib/data/guides/types';
import { getGuideCategoryLabel } from '@/lib/data/guides';

export interface StoneGuidesHubGuide {
  id: string;
  category: GuideCategory;
  content: {
    slug: string;
    title: string;
    description: string;
  };
}

export interface StoneGuidesHubProps {
  guides: StoneGuidesHubGuide[];
  locale: string;
  title: string;
  subtitle: string;
  categories: GuideCategory[];
}

export function StoneGuidesHub({
  guides,
  locale,
  title,
  subtitle,
  categories,
}: StoneGuidesHubProps) {
  const eyebrow = locale === 'fr' ? 'Ressources' : 'Resources';

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900">
      <section className="py-24 md:py-32 px-6 md:px-12 bg-stone-900 text-stone-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-6 font-sans">
            {eyebrow}
          </p>
          <h1
            className="font-serif text-5xl md:text-6xl mb-6 italic leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            {title}
          </h1>
          <p className="text-stone-300 text-lg font-light max-w-2xl mx-auto font-sans leading-relaxed">
            {subtitle}
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto space-y-16 md:space-y-20">
          {categories.map((category) => {
            const categoryGuides = guides.filter((guide) => guide.category === category);
            if (categoryGuides.length === 0) return null;

            return (
              <div key={category}>
                <h2
                  className="font-serif text-2xl md:text-3xl text-stone-900 italic mb-8 pb-4 border-b border-stone-200"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {getGuideCategoryLabel(category, locale)}
                </h2>

                <ul className="divide-y divide-stone-200 border-t border-stone-200">
                  {categoryGuides.map((guide) => (
                    <li key={guide.id}>
                      <Link
                        href={`/${locale}/guides/${guide.content.slug}`}
                        className="group flex flex-col md:flex-row md:items-center justify-between gap-4 py-8 hover:bg-white/80 transition-colors px-2 -mx-2"
                      >
                        <div className="space-y-2 max-w-3xl">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sans">
                            {getGuideCategoryLabel(guide.category, locale)}
                          </p>
                          <h3
                            className="font-serif text-xl md:text-2xl text-stone-900 italic group-hover:text-stone-700 transition-colors"
                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                          >
                            {guide.content.title}
                          </h3>
                          <p className="font-sans text-stone-600 font-light text-sm leading-relaxed line-clamp-2">
                            {guide.content.description}
                          </p>
                        </div>
                        <span className="inline-flex items-center gap-2 shrink-0 text-[10px] uppercase tracking-[0.2em] text-stone-900 font-sans group-hover:gap-3 transition-all">
                          {locale === 'fr' ? 'Lire' : 'Read'}
                          <ArrowRight size={14} strokeWidth={1.5} aria-hidden="true" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default StoneGuidesHub;
